import { parseAmount } from './cost-math.js';

const BASIS_EX = new Set(['ex', 'ex_gst', 'exclusive', 'gst_exclusive', 'excluding_gst', 'excl', 'excl_gst']);
const BASIS_INC = new Set(['inc', 'inc_gst', 'inclusive', 'gst_inclusive', 'including_gst', 'incl', 'incl_gst']);

function normaliseBasisToken(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
}

function parseNullableAmount(...values) {
    for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const parsed = parseAmount(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

function roundCurrency(value) {
    return Math.round(value * 100) / 100;
}

export function resolveScopePricingBasis(item = {}, fallbackIsGstInclusive = true) {
    const rawBasis = normaliseBasisToken(item?.pricingBasis || item?.pricing_basis || item?.amountMode || item?.amount_mode || '');
    if (BASIS_EX.has(rawBasis)) return { isGstInclusive: false, pricingBasis: 'ex_gst' };
    if (BASIS_INC.has(rawBasis)) return { isGstInclusive: true, pricingBasis: 'inc_gst' };
    if (item?.isGstInclusive === false || item?.gstInclusive === false || item?.scopeGstInclusive === false) {
        return { isGstInclusive: false, pricingBasis: 'ex_gst' };
    }
    if (item?.isGstInclusive === true || item?.gstInclusive === true || item?.scopeGstInclusive === true) {
        return { isGstInclusive: true, pricingBasis: 'inc_gst' };
    }
    const inclusive = fallbackIsGstInclusive !== false;
    return { isGstInclusive: inclusive, pricingBasis: inclusive ? 'inc_gst' : 'ex_gst' };
}

export function resolveScopePricing(item = {}, fallbackIsGstInclusive = true) {
    const basis = resolveScopePricingBasis(item, fallbackIsGstInclusive);
    const gstExempt = item?.gstExempt === true || item?.gst_exempt === true;
    // An item priced from its own lines (quote-brain-buildup.ts) is read like a
    // locked one: its amount is the price, never an older quoted stamp.
    const amountLocked = item?.amountLocked === true || item?.amount_locked === true
        || (Array.isArray(item?.quoteBrain?.buildUp) && item.quoteBrain.buildUp.length > 0);
    const displayAmount = parseNullableAmount(item?.amount, item?.estimated_amount);
    let quotedExGst = parseNullableAmount(item?.quoted_amount_ex_gst, item?.amountExGst, item?.amount_ex_gst);
    let quotedIncGst = parseNullableAmount(item?.quoted_amount_inc_gst, item?.amountIncGst, item?.amount_inc_gst);

    if (quotedExGst === null && quotedIncGst !== null) {
        quotedExGst = gstExempt ? quotedIncGst : roundCurrency(quotedIncGst / 1.1);
    }
    if (quotedIncGst === null && quotedExGst !== null) {
        quotedIncGst = gstExempt ? quotedExGst : roundCurrency(quotedExGst * 1.1);
    }
    if (displayAmount !== null) {
        // When the user has explicitly set the amount (amountLocked = true,
        // set by Stage 4's amount-input onBlur handler), `item.amount` is the
        // authoritative price — override any pre-existing quoted_amount_*_gst
        // fields that may have been written by Stage 3 generation or an
        // earlier save. Without this, edits to amount don't propagate to
        // cost-math (which reads pricing.quotedAmountIncGst, not the raw
        // item.amount) — bit Stage 4 PriceTile + cmTotals.items[].incGst,
        // 2026-05-18. The breadcrumb total worked because App.jsx uses
        // deriveQuoteDisplayTotalsFromScope which sums item.amount directly.
        if (amountLocked) {
            if (basis.isGstInclusive) {
                quotedIncGst = displayAmount;
                quotedExGst = gstExempt ? displayAmount : roundCurrency(displayAmount / 1.1);
            } else {
                quotedExGst = displayAmount;
                quotedIncGst = gstExempt ? displayAmount : roundCurrency(displayAmount * 1.1);
            }
        } else if (basis.isGstInclusive) {
            if (quotedIncGst === null) quotedIncGst = displayAmount;
            if (quotedExGst === null) quotedExGst = gstExempt ? displayAmount : roundCurrency(displayAmount / 1.1);
        } else {
            if (quotedExGst === null) quotedExGst = displayAmount;
            if (quotedIncGst === null) quotedIncGst = gstExempt ? displayAmount : roundCurrency(displayAmount * 1.1);
        }
    }

    return {
        ...basis,
        displayAmount,
        quotedAmountExGst: quotedExGst,
        quotedAmountIncGst: quotedIncGst,
        gstExempt,
    };
}

export function attachScopePricingBasis(item, fallbackIsGstInclusive = true) {
    if (!item || typeof item !== 'object') return item;
    const pricing = resolveScopePricing(item, fallbackIsGstInclusive);
    return {
        ...item,
        isGstInclusive: pricing.isGstInclusive,
        pricingBasis: pricing.pricingBasis,
        ...(pricing.quotedAmountExGst !== null ? { quoted_amount_ex_gst: pricing.quotedAmountExGst } : {}),
        ...(pricing.quotedAmountIncGst !== null ? { quoted_amount_inc_gst: pricing.quotedAmountIncGst } : {}),
    };
}
